import {
  Box,
  Button,
  Drawer,
  Hidden,
  IconButton,
  Slide,
  SlideProps,
  Snackbar,
  makeStyles
} from '@material-ui/core';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import React from 'react';

import Logo from '../../assets/img/powered-by.png';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import DetailsPanel from './components/DetailsPanel';
import FilterPanel from './components/FilterPanel';
import ListPanel from './components/ListPanel';
import LoadingIndicator from './components/LoadingIndicator';
import Map from './components/Map';
import { useDistrictFilter } from './useDistrictFilter';

const { REACT_APP_MAPBOX_ACCESS_TOKEN: MAPBOX_ACCESS_TOKEN } = process.env;

const DRAWER_WIDTH = 480;

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: DRAWER_WIDTH,
    background: theme.palette.background.default,
    overflow: 'initial',
    visibility: 'visible !important' as 'visible',
    [theme.breakpoints.down('xs')]: {
      width: `calc(100vw - 28px - ${theme.spacing(1)}px)` // room for toggle button and 1 spacing to right
    }
  },
  map: {
    flexGrow: 1,
    marginLeft: 0
  },
  mapShift: {
    transition: theme.transitions.create('margin', {
      duration: 0,
      delay: theme.transitions.duration.enteringScreen
    }),
    marginLeft: DRAWER_WIDTH,
    [theme.breakpoints.down('xs')]: {
      marginLeft: `calc(100vw - 28px - ${theme.spacing(1)}px)`
    }
  },
  collapsePanelButton: {
    background: theme.palette.background.default,
    borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
    border: `solid 1px ${theme.palette.divider}`,
    left: 'calc(100%)',
    padding: theme.spacing(2, 0.5),
    position: 'absolute',
    top: theme.spacing(8),

    '&:hover': {
      background: theme.palette.background.default
    }
  },
  notice: {
    maxWidth: '80%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%'
    }
  },
  openNotice: {
    marginRight: theme.spacing(-1),
    color: theme.palette.primary.contrastText
  }
}));

const TransitionUp: React.VFC<SlideProps> = (props) => (
  <Slide {...props} direction="up" />
);

const DistrictFilter: React.VFC = () => {
  const classes = useStyles();

  const {
    sidePanel,
    panelVisible,
    districtListings,
    selectedDistrict,
    mapNavigation,
    error,
    isDistrictListingsLoading,
    isNoticeOpen,
    togglePanelVisible,
    applyMapExtentFilter,
    applyFilters,
    selectDistrictById,
    clearSelectedDistrict,
    clearError,
    openFiltersPanel,
    closeFiltersPanel,
    closeNoticeSnackbar,
    openNoticeSnackbar,
    navigateMapToExtent,
    filterPanelConfigurations,
    districtListingsIds
  } = useDistrictFilter();

  const renderSidePanel = (panel: typeof sidePanel) => {
    switch (panel) {
      case 'LIST':
        return (
          <ListPanel
            flex="1 1 0%"
            isLoading={isDistrictListingsLoading}
            results={districtListings}
            onOpenFilters={filterPanelConfigurations && openFiltersPanel} // Only allow opening filter panel when ready
            onSelect={selectDistrictById}
          />
        );
      case 'FILTER':
        return filterPanelConfigurations ? (
          <FilterPanel
            flex="1 1 0%"
            filterConfig={filterPanelConfigurations}
            filterResultsCount={districtListings?.length || 0}
            onClose={closeFiltersPanel}
            onFilterChange={applyFilters}
          />
        ) : null;
      case 'DETAILS':
        return selectedDistrict ? (
          <DetailsPanel
            flex="1 1 0%"
            district={selectedDistrict}
            onLocate={navigateMapToExtent}
            onClose={clearSelectedDistrict}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Box flex="1 1 0%" display="flex" flexDirection="column">
      <Header containerWidth={false} />
      <Box
        component="main"
        flex="1 1 0%"
        position="relative"
        display="flex"
        flexDirection="row"
        mt="75px"
      >
        <Drawer
          classes={{
            paper: classes.drawerPaper
          }}
          PaperProps={{ elevation: 0 }}
          variant="persistent"
          anchor="left"
          open={panelVisible}
        >
          <Box px="2px" py="2px" flex="0 0 auto">
            <LoadingIndicator
              isLoading={isDistrictListingsLoading}
              height={4}
            />
          </Box>
          {renderSidePanel(sidePanel)}
          <IconButton
            className={classes.collapsePanelButton}
            disableRipple
            onClick={togglePanelVisible}
          >
            {panelVisible ? <ArrowLeftIcon /> : <ArrowRightIcon />}
          </IconButton>
        </Drawer>

        <Map
          className={clsx(classes.map, {
            [classes.mapShift]: panelVisible
          })}
          flex="1 1 0%"
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN || ''}
          displayedDistrictsIds={districtListingsIds}
          selectedDistrictId={selectedDistrict?.districtId}
          navigation={mapNavigation}
          isFullPageWidth={!panelVisible}
          onFilterBoundsChange={applyMapExtentFilter}
          onDistrictsClick={([firstDistrictId]) =>
            // TODO. How to handle multiple? Should we always just select first?
            selectDistrictById(firstDistrictId)
          }
        />

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          TransitionComponent={TransitionUp}
          onClose={clearError}
        >
          <Alert severity="error" style={{ width: '100%' }}>
            {error?.message}
          </Alert>
        </Snackbar>
        <Snackbar
          className={classes.notice}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={isNoticeOpen}
          disableWindowBlurListener
          message="NOTICE:  This AWBD GIS Service (AGISS) is for informational purposes only and is not intended for legal, engineering, or surveying purposes. AWBD relies on member information; most district boundary maps and data are primarily from TCEQ and were likely last updated in 2019.  To provide more up-to-date data, email data@awbd-tx.org.  AWBD makes no further claims as to the accuracy or reliability of AGISS data, and neither assumes, nor will accept liability for its use.  The information presented does not represent an on-the-ground survey and represents only the approximate relative location of property boundaries.  Use or reproduction of AGISS data without acknowledging AWBD as the source is strictly prohibited. AWBD does not provide any services for converting the data provided here into other file formats."
          TransitionComponent={TransitionUp}
          action={
            <Button
              color="inherit"
              size="small"
              variant="text"
              onClick={closeNoticeSnackbar}
            >
              Close
            </Button>
          }
        />
      </Box>
      <Footer
        containerWidth={false}
        leftContent={
          <Hidden smDown>
            <Box width="173px">
              <img src={Logo} height={25} alt="powered by lja engineering" />
            </Box>
          </Hidden>
        }
        rightContent={
          <Hidden smDown>
            <Box
              width="173px"
              display="flex"
              flexDirection="row"
              justifyContent="flex-end"
            >
              <Button
                variant="text"
                className={classes.openNotice}
                onClick={openNoticeSnackbar}
              >
                Notice
              </Button>
            </Box>
          </Hidden>
        }
      />
    </Box>
  );
};

export default DistrictFilter;
